import React, { MouseEvent, useContext, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import BusinessIcon from '@mui/icons-material/Business';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListSubheader from '@mui/material/ListSubheader';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoIcon from '@mui/icons-material/Info';
import useMediaQuery from '@mui/material/useMediaQuery';
import classNames from 'classnames';
import Context from '../../context';
import SchemaCompanyName from '../../components/_schema/SchemaCompanyName';
import SchemaCompanyLogoAvatar from '../../components/_schema/SchemaCompanyLogoAvatar';
import SelectSearchCompany from '../../components/_select/SelectSearchCompany';
import { IValue } from '../../components/_select/_module/Select';
import ExternalLinkIconButton from '../../components/_iconButton/ExternalLinkIconButton';
import useKeepitalRoutes from '../../components/_hook/useKeepitalRoutes';
import SuperAdminPermission from '../../adminComponents/_permission/SuperAdminPermission';

const useStyles = makeStyles({
  button: {
    borderRadius: '50px',
  },
  buttonSubtitle: {
    fontSize: '12px',
  },
  iconArrowUp: {
    color: '#1D7EA1',
    fontSize: '24px !important',
  },
  iconArrowDown: {
    fontSize: '24px !important',
  },
  menuItem: {
    height: '48px',
  },
  mainButton: {
    borderRadius: '50px',
  },
  menuPaper: {
    minWidth: '320px',
  },
});

interface IProps {
  logoOnly?: boolean;
  hideCompanyName?: boolean;
  hideDropdownIcon?: boolean;
}

export default function HeaderCompanyButton(props: IProps) {
  const { logoOnly, hideCompanyName, hideDropdownIcon } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { user, company, setCompany, isSuperAdmin } = useContext(Context);
  const isMinWidth1600 = useMediaQuery('(min-width:1650px)');
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const isMenuOpen = Boolean(anchorEl);
  const companies = user.companies
    ? user.companies.map((i) => i.companyData)
    : [];
  const { path: keepitalPath } = useKeepitalRoutes();
  const handleMenuOpen = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuId = 'company-menu';

  const nameLength = isMinWidth1600 ? 40 : 17;

  const menuItems = companies.map((tempCompany) => {
    if (!tempCompany) return null;

    return (
      <MenuItem
        key={tempCompany._id}
        className={classes.menuItem}
        selected={company ? company._id === tempCompany._id : false}
        onClick={() => {
          setCompany(tempCompany._id);
          handleMenuClose();
        }}
      >
        <SchemaCompanyLogoAvatar width={3} height={3} company={tempCompany} />
        &nbsp;&nbsp;
        <SchemaCompanyName company={tempCompany} showVerification />
        &nbsp;&nbsp;
        <ExternalLinkIconButton
          onClick={(e) => e.stopPropagation()}
          tooltip={`${t('company')} ${t('page')}`}
          href={keepitalPath('company.index', {
            origin: tempCompany.origin.toLowerCase(),
            companySlug: tempCompany.slug,
          })}
          target="_blank"
        />
      </MenuItem>
    );
  });
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      id={menuId}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={isMenuOpen}
      classes={{ paper: classes.menuPaper }}
      onClose={handleMenuClose}
    >
      <ListSubheader>
        {t('switch__item__', { item: t('company') })}
      </ListSubheader>
      <SuperAdminPermission hideTooltip>
        <MenuItem
          className={classes.menuItem}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <SelectSearchCompany<IValue>
            fullWidth
            menuPosition="fixed"
            placeholder={t('search')}
            onChange={(option) => {
              setCompany(option.value);
            }}
            value={null}
            useCompanyLogo
            // queryVariableSort={{
            //   logoStatus: -1,
            // }}
            useSearch
          />
          &nbsp;&nbsp;
          <SuperAdminPermission>
            <InfoIcon fontSize="small" color="primary" />
          </SuperAdminPermission>
        </MenuItem>
      </SuperAdminPermission>
      {menuItems}
    </Menu>
  );

  const renderDesktopIconButton = (
    <IconButton
      className={classNames(
        classes.mainButton,
        'default-layout-header-company-button-joyride'
      )}
      color="inherit"
      onClick={handleMenuOpen}
      size="large"
    >
      {company ? (
        <>
          <SchemaCompanyLogoAvatar width={3} height={3} company={company} />

          {!logoOnly && (
            <>
              {!hideCompanyName && (
                <>
                  &nbsp;&nbsp;
                  <Typography variant="body1">
                    <SchemaCompanyName
                      company={company}
                      nameTransform={(name) =>
                        name.length > nameLength
                          ? `${name.substring(0, nameLength)}...`
                          : name
                      }
                      showVerification
                    />
                  </Typography>
                </>
              )}
              <ExternalLinkIconButton
                onClick={(e) => e.stopPropagation()}
                tooltip={`${t('company')} ${t('page')}`}
                href={keepitalPath('company.index', {
                  origin: company.origin.toLowerCase(),
                  companySlug: company.slug,
                })}
                htmlColor="#fff"
                target="_blank"
              />
              {!hideDropdownIcon && <ArrowDropDownIcon />}
            </>
          )}
        </>
      ) : (
        <>
          <BusinessIcon />
          {!logoOnly && (
            <>
              &nbsp;&nbsp;
              <Typography variant="body1">
                {t('select__item__', { item: t('company') })}
              </Typography>
              {!hideDropdownIcon && <ArrowDropDownIcon />}
            </>
          )}
        </>
      )}
    </IconButton>
  );

  if (companies.length === 0) return null;

  return (
    <>
      {renderDesktopIconButton}
      {renderMenu}
    </>
  );
}
